import React, { useEffect, useRef } from 'react';
import { reportWatchTime } from '../utils/secureFunctions';

type WatchContentType = 'post' | 'pulse' | 'testimony';

/**
 * Invisible helper that measures real playback time of the sibling <video> and reports it to the
 * server (counts towards the creator's monetization watch hours). Only time that actually advances
 * while the tab is visible is counted. Remount it (key) when the content changes.
 */
const WatchTracker: React.FC<{ contentType: WatchContentType; contentId: string }> = ({ contentType, contentId }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!contentId) return;
    let pending = 0;
    let lastPos = -1;

    const flush = (min: number) => {
      if (pending < min) return;
      const seconds = Math.min(120, Math.floor(pending));
      pending -= seconds;
      reportWatchTime({ contentType, contentId, seconds }).catch(() => undefined);
    };

    const tick = () => {
      const video = anchorRef.current?.parentElement?.querySelector('video');
      if (!video || document.hidden) return;
      const pos = video.currentTime;
      const playing = !video.paused && !video.ended && video.readyState > 2;
      if (playing && lastPos >= 0) {
        const delta = pos - lastPos;
        // Count real forward progress (or a loop restart), never seeks.
        if (delta > 0 && delta < 2.5) pending += delta;
        else if (delta < 0 && pos < 2) pending += Math.max(0, pos);
      }
      lastPos = pos;
      if (pending >= 15) flush(15);
    };

    const timer = window.setInterval(tick, 1000);
    const onHide = () => { if (document.hidden) flush(3); };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onHide);
      flush(3);
    };
  }, [contentType, contentId]);

  return <span ref={anchorRef} hidden />;
};

export default WatchTracker;
